import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import { decryptToken, parseJWT, validateTokenData, type UserRole } from 'lib/utils/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: UserRole | null;
  userName: string | null;
  isLoading: boolean;
  login: (encryptedToken: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Single source of truth for LMA browser storage keys (keep Logout / LeadManagement / Authorization in sync). */
export const LMA_AUTH_SESSION_KEY = 'lma-auth-token';
export const LMA_USERNAME_LOCAL_KEY = 'lma-username';
export const LMA_USER_REGISTERED_SESSION_KEY = 'lma-user-registered';
/** Approximate epoch ms when the LMA concurrent session slot expires; used to schedule TTL refresh. */
export const LMA_ACTIVE_SESSION_EXPIRES_AT_KEY = 'lma-active-session-expires-at';
export const LMA_IDLE_LOGOUT_SESSION_KEY = 'lma-idle-logout';
/** Set when JWT role is LMASuperAdmin — not counted in concurrent user limit; assertLMASession skips. */
export const LMA_CONCURRENT_EXEMPT_SESSION_KEY = 'lma-concurrent-exempt';

/**
 * Sitecore route segment for the “session ended by administrator” page (same idea as `/Logout` elsewhere).
 * Override with NEXT_PUBLIC_LMA_SESSION_ENDED_BY_ADMIN_PATH if your item name differs.
 */
export const LMA_SESSION_ENDED_BY_ADMIN_PATH_SEGMENT =
  process.env.NEXT_PUBLIC_LMA_SESSION_ENDED_BY_ADMIN_PATH?.trim() || 'SessionEndedByAdmin';

export function getLmaSessionEndedByAdminHref(): string {
  const seg = LMA_SESSION_ENDED_BY_ADMIN_PATH_SEGMENT.replace(/^\/+|\/+$/g, '');
  if (typeof window === 'undefined') return `/${seg}`;
  const pathSegs = window.location.pathname.split('/').filter(Boolean);
  if (pathSegs.length >= 1 && /^[a-z]{2}(-[a-z]{2})?$/i.test(pathSegs[0])) {
    return `/${pathSegs[0]}/${seg}`;
  }
  return `/${seg}`;
}

const TOKEN_STORAGE_KEY = LMA_AUTH_SESSION_KEY;
const USERNAME_STORAGE_KEY = LMA_USERNAME_LOCAL_KEY;

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initializedRef = useRef(false);
  const tokenRemovedRef = useRef(false);

  const login = useCallback(async (encryptedToken: string): Promise<boolean> => {
    try {
      // Decrypt the token
      const decryptedToken = await decryptToken(encryptedToken);

      // Parse the JWT
      const tokenData = parseJWT(decryptedToken);

      // Validate token data
      if (!validateTokenData(tokenData)) {
        console.error('Token validation failed');
        return false;
      }

      // Set authentication state
      setIsAuthenticated(true);

      // Resolve domain-prefixed role (e.g. "sitecore\LMASuperAdmin") to clean UserRole
      const resolvedRole: UserRole = tokenData!.Role.includes('LMASuperAdmin')
        ? 'LMASuperAdmin'
        : 'AvacareLeadManager';
      setUserRole(resolvedRole);

      setUserName(tokenData!.UserName);

      // Store encrypted token in sessionStorage (clears on tab close)
      sessionStorage.setItem(TOKEN_STORAGE_KEY, encryptedToken);

      // Store username in localStorage for audit purposes (persists)
      localStorage.setItem(USERNAME_STORAGE_KEY, tokenData!.UserName);

      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    // Clear authentication state
    setIsAuthenticated(false);
    setUserRole(null);
    setUserName(null);

    // Clear session storage (token)
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);

    // Note: We keep username in localStorage for audit purposes
    // If you want to clear it on logout, uncomment the line below:
    // localStorage.removeItem(USERNAME_STORAGE_KEY);
  }, []);

  // Check for token in query params and session storage on mount
  // Only run once on initial mount to prevent flash/re-initialization
  useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current) return;
    initializedRef.current = true;

    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Check query params for token (using window.location to avoid useSearchParams issues)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromQuery = urlParams.get('token');

        if (tokenFromQuery && !tokenRemovedRef.current) {
          // Token found in query params, try to decrypt and validate
          await login(tokenFromQuery);

          // Remove token from URL immediately for security (regardless of success/failure)
          if (isMounted) {
            tokenRemovedRef.current = true;

            // Remove token from URL using window.history.replaceState
            // This is the most reliable method across all environments
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('token');
            const cleanUrl = newUrl.pathname + (newUrl.search ? newUrl.search : '');
            window.history.replaceState({}, document.title, cleanUrl);

            // Use Next.js router.replace with the clean URL we already computed
            // Use window.location.pathname (actual browser URL) not router.pathname (internal route)
            router.replace(cleanUrl, cleanUrl, { shallow: true });
          }
        } else {
          // No token in query, check session storage (persists across reloads)
          const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
          if (storedToken && isMounted) {
            // Token exists in session, validate and restore authentication state
            try {
              const decryptedToken = await decryptToken(storedToken);
              const tokenData = parseJWT(decryptedToken);

              if (validateTokenData(tokenData) && isMounted) {
                // Valid token, restore authentication state in a single batch
                setIsAuthenticated(true);

                // Resolve domain-prefixed role to clean UserRole
                const resolvedRole: UserRole = tokenData!.Role.includes('LMASuperAdmin')
                  ? 'LMASuperAdmin'
                  : 'AvacareLeadManager';
                setUserRole(resolvedRole);
                setUserName(tokenData!.UserName);
              } else if (isMounted) {
                // Invalid or expired token, clear storage
                console.warn('Token validation failed, clearing session');
                logout();
              }
            } catch (error) {
              console.error('Token validation error on reload:', error);
              if (isMounted) {
                // Invalid token, clear storage
                logout();
              }
            }
          } else if (isMounted) {
            // No token found in sessionStorage (tab was closed and reopened)
            // User is not authenticated
            setIsAuthenticated(false);
            setUserRole(null);
            setUserName(null);
          }
        }

        // Load username from localStorage for audit (persists across sessions)
        // This is separate from the token validation above
        if (isMounted) {
          const storedUserName = localStorage.getItem(USERNAME_STORAGE_KEY);
          if (storedUserName) {
            setUserName((prev) => prev || storedUserName);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          logout();
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - login/logout are stable callbacks

  // Listen for storage events to handle cross-tab logout
  // Note: sessionStorage automatically clears when tab closes, so we don't need beforeunload
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // This only fires for changes from other tabs/windows
      // For same-tab changes, we handle it directly
      if (e.key === LMA_AUTH_SESSION_KEY && !e.newValue && e.oldValue) {
        // Token was cleared in another tab, logout
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [logout]);

  // Safeguard: Monitor URL and remove token if it reappears after initial removal
  // This handles edge cases where Next.js routing might re-add the token during hydration
  useEffect(() => {
    if (!tokenRemovedRef.current) return;

    const checkAndRemoveToken = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('token')) {
        urlParams.delete('token');
        const cleanUrl =
          window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    // Check immediately
    checkAndRemoveToken();

    // Also check on popstate (browser back/forward)
    window.addEventListener('popstate', checkAndRemoveToken);

    return () => {
      window.removeEventListener('popstate', checkAndRemoveToken);
    };
  }, [router.asPath]);

  const value: AuthContextType = {
    isAuthenticated,
    userRole,
    userName,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
}
